/**
 * CycleSummary — a cycle at a glance: the card that opens over a cycle's name
 * (ObjectRef `summary`).
 *
 * Where the cycle is on its path (Draft → Ready → Active → Ratified →
 * Published → Closed, CYCLE_LIFECYCLE_PATH) and the day it reached each step,
 * who owns it, and where each of its tracks stands. The status is the one the
 * Cycles list shows — both read it through fetchCycleLifecycles.
 *
 * It reads its own data when it opens, so any page that names a cycle can
 * hand it to ObjectRef with just the id.
 */
import { useEffect, useMemo } from 'react';
import { PopoverRows, type PopoverRow, type PopoverRowState } from '@/components/ds/Popover';
import { useCycleLifecycle } from '@/hooks/prioritization/useCycleLifecycleDisplayMap';
import { useUserNameMap } from '@/hooks/useUserNameMap';
import { formatDate } from '@/i18n/formatters';
import { cycleLifecyclePath, type CyclePathStep } from '@/lib/cycleLifecycleDisplayStatus';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { TRACK_STATUS_LABEL } from '@/types/prioritization/track';

const HEADING = 'Cycle · how it got here';

function day(at: string | null): string {
  return at ? formatDate(at, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
}

/** Draft, Ready and Active are stretches of work: the one it is on is partly done. The rest are events: reached is done. */
const WORK_STEPS = new Set(['draft', 'ready', 'active']);

function pathRow(step: CyclePathStep): PopoverRow {
  const when = day(step.at);
  if (step.state === 'now') {
    const state: PopoverRowState = WORK_STEPS.has(step.status) ? 'partial' : 'done';
    return { label: step.label, state, value: <strong className="font-medium">Now</strong>, note: when ? `Since ${when}` : undefined };
  }
  if (step.state === 'done') return { label: step.label, state: 'done', value: when || 'Date not recorded' };
  if (step.state === 'skipped') return { label: step.label, state: 'open', value: <span className="text-[var(--text-dim)]">Not reached</span> };
  return { label: step.label, state: 'open', value: <span className="text-[var(--text-dim)]">Not yet</span> };
}

export function CycleSummary({ cycleId }: { cycleId: string }) {
  const read = useCycleLifecycle(cycleId);
  const storeOwnerId = useDegapProcessStore((s) => s.cycles.find((c) => c.id === cycleId)?.ownerId ?? null);
  const ownerId = read?.ownerId ?? storeOwnerId;
  const names = useUserNameMap([ownerId]);

  const allTracks = useTrackStore((s) => s.tracks);
  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);
  const tracks = useMemo(
    () => allTracks.filter((t) => t.cycleId === cycleId && t.status !== 'archived'),
    [allTracks, cycleId],
  );
  const haveTracks = tracks.length > 0;
  useEffect(() => {
    if (!haveTracks) void fetchTracksForCycle(cycleId);
    // Once per cycle: a cycle with no tracks should not refetch on every store change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId]);

  const link = { label: 'Open the cycle', href: `/prioritization/cycles/${cycleId}` };
  if (read === undefined) {
    return <PopoverRows heading={HEADING} rows={[]} footnote="Reading the cycle…" link={link} />;
  }
  if (read === null) {
    return <PopoverRows heading={HEADING} rows={[]} footnote="This cycle could not be read." link={link} />;
  }

  // No design pass: Ready is not a step this cycle has, so it is left out rather than shown as not reached.
  const path = cycleLifecyclePath(read.signals, read.dates).filter((s) => !(s.status === 'ready' && s.state === 'skipped'));
  const owner = ownerId ? names[ownerId] ?? 'Someone not loaded here' : 'No owner set';

  const rows: PopoverRow[] = [
    { label: 'Owner', value: owner },
    ...path.map(pathRow),
    {
      label: 'Tracks',
      value:
        tracks.length === 0 ? (
          <span className="text-[var(--text-dim)]">No tracks yet</span>
        ) : (
          <ul className="space-y-0.5">
            {tracks.map((t) => (
              <li key={t.id} className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 truncate">{t.name}</span>
                <span className="whitespace-nowrap text-[12.5px] text-[var(--text-dim)]">{TRACK_STATUS_LABEL[t.status] ?? t.status}</span>
              </li>
            ))}
          </ul>
        ),
    },
  ];

  return (
    <div data-testid="cycle-summary" data-status={read.status}>
      <PopoverRows
        heading={HEADING}
        rows={rows}
        footnote={read.status === 'paused' ? 'Paused where it stands.' : undefined}
        link={link}
      />
    </div>
  );
}
